import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useCreate, useNotification } from "@refinedev/core";
import { FormEvent, useState } from "react";
import { neutral } from "style/color";
import { isValidEmail, EMAIL_VALIDATION_ERROR_MESSAGE } from "utils/valid-email";
import awsExports from "aws-exports";
import { axiosInstance } from "providers/rest-data-provider/utils";
import { FundAccountMember } from "interfaces/fund-account-member";
import { IUser } from "interfaces/user";

type InviteRole = "ADMIN" | "ADVISOR" | "VIEWER";

const ROLE_DESCRIPTIONS: Record<
  InviteRole,
  { title: string; description: string }
> = {
  ADMIN: {
    title: "Admin",
    description:
      "Has full access to the fund and can invite or remove others or modify the succession plan",
  },
  ADVISOR: {
    title: "Advisor",
    description: "Can recommend transactions for the fund",
  },
  VIEWER: {
    title: "Viewer",
    description: "Can view all information but cannot perform any action",
  },
};

export interface InviteFundUserModalProps {
  open: boolean;
  onClose: () => void;
  fundAccountId: number;
  fundAccountMembers: FundAccountMember[] | undefined;
  onInviteSent: () => void;
}

export const InviteFundUserModal: React.FC<InviteFundUserModalProps> = ({
  open,
  onClose,
  fundAccountMembers,
  fundAccountId,
  onInviteSent,
}) => {
  const { mutate, isLoading } = useCreate();
  const { open: openNotification } = useNotification();
  const [email, setEmail] = useState("");
  const [emailFormatError, setEmailFormatError] = useState(false);
  const [role, setRole] = useState<InviteRole>("ADMIN");

  const notifyError = (message: string) => {
    openNotification?.({ message, type: "error" });
  };

  const resetForm = () => {
    setEmail("");
    setEmailFormatError(false);
    setRole("ADMIN");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      notifyError("Email is required");
      return;
    }
    if (!isValidEmail(trimmed)) {
      notifyError("Please enter a valid email address");
      return;
    }
    const normalized = trimmed.toLowerCase();
    const alreadyMember = fundAccountMembers?.some(
      (m) => m.user.email.toLowerCase() === normalized
    );
    if (alreadyMember) {
      notifyError("The user is already member of the fund account");
      return;
    }

    const apiUrl = awsExports.endpoints[0].endpoint as string;
    try {
      const { data: userByEmail } = await axiosInstance.get<IUser | Record<string, never>>(
        `${apiUrl}/users/email/${encodeURIComponent(trimmed)}`
      );
      if (
        userByEmail &&
        "email" in userByEmail &&
        userByEmail.email &&
        "status" in userByEmail &&
        userByEmail.status !== "ACCEPTED"
      ) {
        notifyError(
          "This user cannot be invited because their account is not in ACCEPTED status."
        );
        return;
      }
    } catch {
      notifyError("Unable to verify this email. Please try again.");
      return;
    }

    mutate(
      {
        resource: "fund-account-invitation",
        values: {
          email: trimmed,
          fundId: fundAccountId,
          role,
        },
        successNotification: () => ({
          message: "Invite has been successfully sent",
          type: "success",
        }),
        errorNotification: (error) => ({
          message:
            (typeof error?.message === "string" && error.message.length > 0
              ? error.message
              : "Something went wrong. Please try again.") as string,
          type: "error",
        }),
      },
      {
        onSuccess: () => {
          resetForm();
          onInviteSent();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={submit} autoComplete="off">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span">
            Invite others to manage this fund, assign them roles and set their
            permissions.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            name="email"
            label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailFormatError(e.target.value ? !isValidEmail(e.target.value) : false);
            }}
            onBlur={(e) => setEmailFormatError(e.target.value ? !isValidEmail(e.target.value) : false)}
            error={emailFormatError}
            helperText={emailFormatError ? EMAIL_VALIDATION_ERROR_MESSAGE : undefined}
            type="text"
            required
            fullWidth
            margin="normal"
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle3" sx={{ mb: 1 }}>
            Assign role
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              name="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as InviteRole)}
            >
              {(Object.keys(ROLE_DESCRIPTIONS) as InviteRole[]).map((r) => (
                <FormControlLabel
                  key={r}
                  value={r}
                  sx={{
                    mb: 1,
                    alignItems: "flex-start",
                    "& span.MuiRadio-root": { paddingTop: 0 },
                  }}
                  control={<Radio />}
                  label={
                    <>
                      <Typography variant="bodyCopyBold">
                        {ROLE_DESCRIPTIONS[r].title}
                      </Typography>
                      <br />
                      <Typography
                        sx={{ color: neutral[600] }}
                        variant="bodyCopyRegular"
                      >
                        {ROLE_DESCRIPTIONS[r].description}
                      </Typography>
                    </>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" color="primary" size="large" onClick={handleClose}>
            Discard
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            Send invite
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
